import React, { useEffect, useMemo, useState } from 'react';
import {
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

type Severity = 'info' | 'warn' | 'error' | 'critical';

interface LogEntry {
  id: number | string;
  created_at: string;
  severity: Severity;
  source: string;
  message: string;
  meta?: Record<string, any>;
}

const SEVERITIES: { key: Severity | 'all'; label: string }[] = [
  { key: 'all', label: 'Alle' },
  { key: 'info', label: 'Info' },
  { key: 'warn', label: 'Warnung' },
  { key: 'error', label: 'Fehler' },
  { key: 'critical', label: 'Kritisch' },
];

const sevTone = (s: Severity) => {
  switch (s) {
    case 'critical':
      return 'bg-rose-500/10 border-rose-500/40 text-rose-700 dark:text-rose-300';
    case 'error':
      return 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300';
    case 'warn':
      return 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300';
    default:
      return 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300';
  }
};

const sevIcon = (s: Severity) => {
  switch (s) {
    case 'critical':
    case 'error':
      return <XCircleIcon className="w-4 h-4" />;
    case 'warn':
      return <ExclamationTriangleIcon className="w-4 h-4" />;
    default:
      return <InformationCircleIcon className="w-4 h-4" />;
  }
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const monthYearLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

const AdminLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<Severity | 'all'>('all');
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/admin/logs');
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : Array.isArray(res.data) ? res.data : []);
      setEndpointMissing(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setEndpointMissing(true);
        setLogs([]);
      } else {
        setError(err.response?.data?.message || err.message || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: logs.length, info: 0, warn: 0, error: 0, critical: 0 };
    for (const l of logs) acc[l.severity] = (acc[l.severity] || 0) + 1;
    return acc;
  }, [logs]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (severity !== 'all' && l.severity !== severity) return false;
      if (s && !`${l.source} ${l.message}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [logs, severity, search]);

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
        {/* HERO */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            Admin · Systemlogs · Stand {monthYearLabel}
          </div>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight"
            style={{ letterSpacing: '-0.04em' }}
          >
            Was<br />
            <span className="text-amber-600 dark:text-amber-400">passiert ist.</span>
          </h1>
          <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
            Letzte Server-Aktivitäten, Fehler und kritische Ereignisse — chronologisch, filterbar nach Schweregrad und durchsuchbar.
          </p>
        </section>

        {/* FILTER + SEARCH */}
        <section className="pb-4">
          <div className="rounded-3xl p-4 md:p-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-600 dark:text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nach Quelle oder Nachricht suchen…"
                  className="w-full h-11 sm:h-12 pl-11 pr-4 rounded-2xl text-sm font-semibold text-gray-900 dark:text-white placeholder-slate-500 outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                />
              </div>
              {/* Reload */}
              <button
                onClick={fetchLogs}
                className="h-11 sm:h-12 px-4 rounded-2xl text-xs font-semibold text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/60 hover:bg-gray-100 dark:hover:bg-slate-900/80 transition flex items-center gap-2 justify-center"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Neu laden
              </button>
            </div>
            {/* Severity-Tabs */}
            <div className="flex flex-wrap items-center gap-1.5 mt-4">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mr-1 shrink-0">Filter</span>
              {SEVERITIES.map((s) => {
                const active = severity === s.key;
                const count = counts[s.key] ?? 0;
                return (
                  <button
                    key={s.key}
                    onClick={() => setSeverity(s.key)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                      active
                        ? 'bg-primary-500 text-slate-950'
                        : 'bg-gray-100 dark:bg-slate-900/55 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                    }`}
                  >
                    {s.label} <span className={active ? 'opacity-70' : 'text-gray-500 dark:text-slate-500'}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* LIST */}
        <section className="pb-6">
          {loading ? (
            <div className="rounded-3xl p-10 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 text-center text-gray-600 dark:text-slate-400">
              <ArrowPathIcon className="w-6 h-6 mx-auto mb-3 animate-spin" />
              Lade Logs…
            </div>
          ) : endpointMissing ? (
            <div className="rounded-3xl p-10 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 text-center">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
                <InformationCircleIcon className="w-6 h-6 text-amber-700 dark:text-amber-300" />
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white">Logging-Endpoint nicht aktiv</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 max-w-md mx-auto mt-2">
                <code className="font-mono text-xs">/api/admin/logs</code> antwortet mit 404. Sobald das Backend Logs streamt, erscheinen sie automatisch hier.
              </p>
            </div>
          ) : error ? (
            <div className="rounded-3xl p-6 bg-rose-500/5 border border-rose-500/30 flex items-start gap-3">
              <XCircleIcon className="w-5 h-5 text-rose-700 dark:text-rose-300 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-rose-700 dark:text-rose-300">Logs konnten nicht geladen werden</div>
                <div className="text-xs text-gray-600 dark:text-slate-400 mt-1">{error}</div>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl p-10 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 text-center">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-6 h-6 text-emerald-700 dark:text-emerald-300" />
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white">
                {logs.length === 0 ? 'Alles ruhig — keine Einträge.' : 'Keine Treffer'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 max-w-md mx-auto mt-2">
                {logs.length === 0
                  ? 'Es liegen aktuell keine Server-Ereignisse vor.'
                  : 'Filter zurücksetzen oder Suchbegriff anpassen.'}
              </p>
            </div>
          ) : (
            <div className="rounded-3xl bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 border-b border-gray-100 dark:border-slate-800">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <ClipboardDocumentListIcon className="w-4 h-4 text-amber-700 dark:text-amber-300" />
                </div>
                <h4 className="text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-slate-500 flex-1">
                  {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}
                </h4>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-slate-800">
                {filtered.map((l) => (
                  <div key={l.id} className="px-4 py-3 sm:px-5 sm:py-3.5 flex items-start gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${sevTone(l.severity)}`}>
                      {sevIcon(l.severity)}
                      {l.severity.toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="font-semibold text-gray-900 dark:text-white">{l.source}</span>
                        <span className="text-gray-500 dark:text-slate-500 tabular-nums">· {formatTime(l.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-slate-300 mt-0.5 break-words">{l.message}</p>
                      {l.meta && (
                        <pre className="mt-2 text-[10px] font-mono text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700 rounded-lg p-2 overflow-x-auto">
                          {JSON.stringify(l.meta, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminLogs;
